<script setup>
import { useAuthStore } from "@/stores";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import {
  BriefcaseIcon,
  FolderIcon,
  PresentationChartLineIcon,
  ShoppingCartIcon,
  SpeakerphoneIcon,
  VideoCameraIcon,
  ViewListIcon,
} from "@heroicons/vue/outline";
import { computed } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();
const authStore = useAuthStore();
const isSuperAdmin = computed(() => authStore.isSuperAdmin);
const isAccountManager = computed(() => authStore.isAccountManager);
const isLearner = computed(() => authStore.isLearner);
const isMultiStoreManager = computed(() => authStore.isMultiStoreManager);
const routes = [
  {
    name: "Manage Badge",
    to: "/manage/badges",
    urlType: "lms",
  },
  {
    name: "Manage Users",
    to: "/central/manage-users",
    urlType: "central",
  },
  {
    name: "Manage Curriculums",
    to: "/manage/curriculums",
    urlType: "lms",
  },
  {
    name: "Manage Courses",
    to: "/manage/courses",
    urlType: "lms",
  },
  {
    name: "Manage Categories",
    to: "/manage/categories",
    urlType: "lms",
  },
  {
    name: "Manage Playlist",
    to: "/manage/playlist",
    urlType: "lms",
  },
  {
    name: "Manage Companies",
    to: "/central/companies",
    urlType: "central",
  },
  {
    name: "Manage Stores",
    to: "/central/stores",
    urlType: "central",
  },
  {
    name: "Manage Banner",
    to: "/banner",
    urlType: "lms",
  },
  {
    name: "Usage Report",
    to: "/usage-report",
    urlType: "lms",
  },
];

const filteredRoutes = computed(() => {
  return routes.filter((route) => {
    if (!isSuperAdmin.value && route.name.toLowerCase() === "manage badge") {
      return false;
    }

    if (
      !isSuperAdmin.value &&
      route.name.toLowerCase() === "manage companies"
    ) {
      return false;
    }

    if (!isSuperAdmin.value && route.name.toLowerCase() === 'manage banner') {
      return false;
    }

    if (
      !isSuperAdmin.value &&
      !isAccountManager.value &&
      !isMultiStoreManager.value &&
      route.name.toLowerCase() === "manage stores"
    ) {
      return false;
    }

    if (isLearner.value && route.name.toLowerCase() === "manage users") {
      return false;
    }

    if (
      !isSuperAdmin.value &&
      !isAccountManager.value &&
      route.name.toLowerCase() === "manage courses"
    ) {
      return false;
    }

    if (
      !isSuperAdmin.value &&
      !isAccountManager.value &&
      route.name.toLowerCase() === "manage playlist"
    ) {
      return false;
    }

    if (
      !isSuperAdmin.value &&
      route.name.toLowerCase() === "manage curriculums"
    ) {
      return false;
    }

    if (!isSuperAdmin.value && route.name.toLowerCase() === "usage report") {
      return false;
    }

    if (!isSuperAdmin.value && route.name.toLowerCase() === "manage categories") {
      return false;
    }

    return true;
  });
});
</script>
<template>
  <Menu as="div" class="relative ml-3">
    <div v-bind="$attrs" class="flex items-center justify-center h-full">
      <MenuButton
        class="relative max-w-xs text-kintz-primary-4 rounded-full bg-kintz-primary-1 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-kintz-primary-2 focus-visible:ring-offset-2 inline-flex items-center px-1 text-sm font-medium"
      >
        More...
      </MenuButton>
    </div>
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        class="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-kintz-primary-1 py-1 shadow-lg ring-1 ring-black focus:outline-hidden"
      >
        <MenuItem class="md:hidden">
          <router-link
            :to="{ name: 'reports' }"
            class="block px-4 py-2 text-sm bg-kintz-primary-1"
            :class="{
              'text-kintz-primary-2': route.name === 'reports',
              'text-kintz-primary-4': route.name !== 'reports',
            }"
          >
            <PresentationChartLineIcon class="mr-2 h-5 w-5 inline" />Reports
          </router-link>
        </MenuItem>
        <MenuItem class="lg:hidden" v-if="!isLearner">
          <router-link
            :to="{ name: 'active-role-plays' }"
            class="block px-4 py-2 text-sm bg-kintz-primary-1"
            :class="{
              'text-kintz-primary-2': route.name === 'active-role-plays',
              'text-kintz-primary-4': route.name !== 'active-role-plays',
            }"
          >
            <VideoCameraIcon class="mr-2 h-5 w-5 inline" />Active Role Plays
          </router-link>
        </MenuItem>
        <MenuItem class="xl:hidden">
          <router-link
            :to="{ name: 'filemanager' }"
            class="block px-4 py-2 text-sm bg-kintz-primary-1"
            :class="{
              'text-kintz-primary-2': route.name === 'filemanager',
              'text-kintz-primary-4': route.name !== 'filemanager',
            }"
          >
            <FolderIcon class="mr-2 h-5 w-5 inline" />Resource Manager
          </router-link>
        </MenuItem>
        <MenuItem class="xl:hidden">
          <router-link
            :to="{ name: 'Announcement Home Page' }"
            class="block px-4 py-2 text-sm bg-kintz-primary-1"
            :class="{
              'text-kintz-primary-2': route.name === 'Announcement Home Page',
              'text-kintz-primary-4': route.name !== 'Announcement Home Page',
            }"
          >
            <SpeakerphoneIcon class="mr-2 h-5 w-5 inline" />News Feed
          </router-link>
        </MenuItem>
        <MenuItem
          v-for="item in filteredRoutes.filter(
            (item) => item.urlType === 'lms'
          )"
          :key="item.name"
          class="2xl:hidden"
        >
          <router-link
            :to="item.to"
            class="block px-4 py-2 text-sm bg-kintz-primary-1"
            :class="{
              'text-kintz-primary-2': route.fullPath === item.to,
              'text-kintz-primary-4': route.fullPath !== item.to,
            }"
          >
            <ViewListIcon class="mr-2 h-5 w-5 inline" />
            {{ item.name }}
          </router-link>
        </MenuItem>
        <MenuItem
          v-for="item in filteredRoutes.filter(
            (item) => item.urlType !== 'lms'
          )"
          :key="item.name"
          class="2xl:hidden"
        >
          <a
            :href="item.to"
            target="_blank"
            class="w-full px-4 py-2 text-sm flex justify-start items-start hover:text-kintz-primary-2 text-kintz-primary-4"
          >
            <ViewListIcon class="mr-2 h-5 w-5 inline" /> {{ item.name }}</a
          >
        </MenuItem>
        <MenuItem>
          <a
            href="https://kintzgroup.com/shop/"
            target="_blank"
            class="block px-4 py-2 text-sm bg-kintz-primary-1"
            :class="{
              'text-kintz-primary-2': route.name === 'filemanager',
              'text-kintz-primary-4': route.name !== 'filemanager',
            }"
          >
            <ShoppingCartIcon class="mr-2 h-5 w-5 inline" />Shop
          </a>
        </MenuItem>
        <MenuItem>
          <a
            href="https://kintzgroup.com/product-category/workshops/"
            target="_blank"
            class="block px-4 py-2 text-sm bg-kintz-primary-1"
            :class="{
              'text-kintz-primary-2': route.name === 'filemanager',
              'text-kintz-primary-4': route.name !== 'filemanager',
            }"
          >
            <BriefcaseIcon class="mr-2 h-5 w-5 inline" />Workshops
          </a>
        </MenuItem>
      </MenuItems>
    </transition>
  </Menu>
</template>

<style scoped></style>
